import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import {
  type CompositeLumberComponent,
  type CustomFramingComponent,
  type DimensionalLumberComponent,
  type IJoistComponent,
  type TrussComponent,
  FramingComponentType,
} from "building-types/framing/framingComponentTypes";
import type { IJoistProfileType } from "building-types/framing/iJoistTypes";
import type { LumberProfileType } from "building-types/framing/lumberTypes";
import uid from "common/utils/uid";

export function framingComponentCopy(component: FramingComponentEntity, name: string): FramingComponentEntity {
  return {
    ...component,
    name,
  };
}

export function framingComponentNew(name: string, header: FramingComponentEntity): FramingComponentEntity {
  return {
    ...header,
    name,
  };
}

export function framingComponentWithLumberProfile(
  name: string,
  lumberProfileType: LumberProfileType,
  id: string = uid(),
  sortIndex: number = 0,
): DimensionalLumberComponent {
  return {
    entityType: "FRAMING_COMPONENT",
    id,
    sortIndex,
    type: FramingComponentType.DIMENSIONAL_LUMBER,
    lumberProfileType,
    name,
  };
}

export function framingComponentWithCompositeSize(
  name: string,
  depth: number,
  width: number,
  id: string = uid(),
  sortIndex: number = 0,
): CompositeLumberComponent {
  return {
    entityType: "FRAMING_COMPONENT",
    id,
    sortIndex,
    type: FramingComponentType.COMPOSITE_LUMBER,
    depth,
    width,
    name,
  };
}

export function framingComponentWithIJoistProfile(
  name: string,
  iJoistProfileType: IJoistProfileType,
  id: string = uid(),
  sortIndex: number = 0,
): IJoistComponent {
  return {
    entityType: "FRAMING_COMPONENT",
    id,
    sortIndex,
    type: FramingComponentType.I_JOIST,
    iJoistProfileType,
    name,
  };
}

export function framingComponentWithCustomSize(
  name: string,
  depth: number,
  width: number,
  id: string = uid(),
  sortIndex: number = 0,
): CustomFramingComponent {
  return {
    entityType: "FRAMING_COMPONENT",
    id,
    sortIndex,
    type: FramingComponentType.CUSTOM,
    depth,
    width,
    name,
  };
}

export function framingComponentWithTrussSize(
  name: string,
  depth: number,
  width: number,
  id: string = uid(),
  sortIndex: number = 0,
): TrussComponent {
  return {
    entityType: "FRAMING_COMPONENT",
    id,
    sortIndex,
    type: FramingComponentType.TRUSS,
    depth,
    width,
    name,
  };
}

export function isDimensionalLumberComponent(
  component: FramingComponentEntity,
): component is DimensionalLumberComponent {
  return component.type === FramingComponentType.DIMENSIONAL_LUMBER;
}

export function isCompositeLumberComponent(component: FramingComponentEntity): component is CompositeLumberComponent {
  return component.type === FramingComponentType.COMPOSITE_LUMBER;
}

export function isIJoistComponent(component: FramingComponentEntity): component is IJoistComponent {
  return component.type === FramingComponentType.I_JOIST;
}

export function isCustomFramingComponent(component: FramingComponentEntity): component is CustomFramingComponent {
  return component.type === FramingComponentType.CUSTOM;
}

export function isTrussComponent(component: FramingComponentEntity): component is TrussComponent {
  return component.type === FramingComponentType.TRUSS;
}
